import React, { useRef, useState, useEffect } from 'react';
import { useIntersection } from '../hooks/useScrolled';
import './ExclusiveDesigns.css';

export default function ExclusiveDesigns() {
  const ref = useRef(null);
  const visible = useIntersection(ref);

  const [collections, setCollections] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(4);

  // 🔥 Responsive items
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 520) {
        setItemsPerPage(1);
      } else if (window.innerWidth <= 900) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 🔥 Fetch API
  useEffect(() => {
    fetch("https://admin.jksjewels.com/wp-json/wp/v2/productions")
      .then(res => res.json())
      .then(data => {
        const formatted = data.map(item => ({
          id: item.id,
          image: item.acf?.image,
          alt: item.acf?.alt || "Jewellery",
          brand: item.acf?.brand || "JKS",
          sub: item.acf?.sub_text || "Collections"
        }));
        setCollections(formatted);
      })
      .catch(err => console.error(err));
  }, []);

  // 🔁 Clone for infinite
  const extendedCollections = [
    ...collections.slice(-itemsPerPage),
    ...collections,
    ...collections.slice(0, itemsPerPage)
  ];

  // Start from real first
  useEffect(() => {
    if (collections.length) {
      setCurrentIndex(itemsPerPage);
    }
  }, [collections, itemsPerPage]);

  // ▶ Auto slide
  useEffect(() => {
    if (!collections.length || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => prev + 1);
    }, 3500);

    return () => clearInterval(interval);
  }, [collections, isPaused]);

  // 🔁 Infinite reset logic
  useEffect(() => {
    if (!collections.length) return;

    if (currentIndex === collections.length + itemsPerPage) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(itemsPerPage);
      }, 600);
    }

    if (currentIndex === 0) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(collections.length);
      }, 600);
    }
  }, [currentIndex, collections.length, itemsPerPage]);

  // 🔄 Restore transition
  useEffect(() => {
    if (!isTransitioning) {
      requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
    }
  }, [isTransitioning]);

  // ⬅➡ Controls
  const next = () => setCurrentIndex(prev => prev + 1);
  const prev = () => setCurrentIndex(prev => prev - 1);

  if (!collections.length) return null;

  return (
    <section className="exclusive" ref={ref} id="jewellery">

      {/* Header */}
      <div className={`exclusive__header${visible ? ' visible' : ''}`}>
        <span className="exclusive__label">Dazzling Charisma</span>
        <h2 className="exclusive__title">
          <em>Exclusive Designs</em>
        </h2>
        <p className="exclusive__sub">
          Today, JKS Jewels is a vendor for most of the retail corporate stores in India, as well as exporting its jewellery to the Middle East, Europe, and Singapore...
        </p>
      </div>

      {/* Carousel */}
      <div
        className="exclusive__carousel-wrap"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >

        {/* Prev */}
        <button className="exclusive__arrow exclusive__arrow--prev" onClick={prev}>
          ‹
        </button>

        {/* Viewport */}
        <div className="exclusive__viewport">
          <div
            className="exclusive__track"
            style={{
              transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)`,
              transition: isTransitioning ? "transform 0.6s ease" : "none"
            }}
          >
            {extendedCollections.map((col, i) => (
              <div key={i} className="exclusive__card">
                <div className="exclusive__card-img">
                  <img src={col.image} alt={col.alt} />
                  <div className="exclusive__card-overlay" />
                </div>

                <div className="exclusive__card-label">
                  <span className="exclusive__card-brand">{col.brand}</span>
                  <span className="exclusive__card-sub">{col.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next */}
        <button className="exclusive__arrow exclusive__arrow--next" onClick={next}>
          ›
        </button>

      </div>
      <div className="exclusive__cta">
        <a href="/products" className="exclusive__btn">
          View All Products
        </a>
      </div>
    </section>
  );
}